import { Star, Quote, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

// Real Google reviews from public Google Business profile
// Source: Google Search / Recovery Connections of Central Florida (4.9 ★ · 64 reviews)
const REVIEWS = [
  {
    name: "Kimberly Carlyle",
    initials: "KC",
    rating: 5,
    text: "A great atmosphere and great group of people working towards a great cause! The staff truly care and it shows in everything they do.",
    timeAgo: "a year ago",
  },
  {
    name: "William Lammert",
    initials: "WL",
    rating: 5,
    text: "He was able to help with getting my Florida ID and switching over my Medicaid. Practical, real-world help — exactly what I needed at a really hard moment.",
    timeAgo: "2 years ago",
  },
  {
    name: "Bradley",
    initials: "BR",
    rating: 5,
    text: "Amazing staff, every single one of them. I've never felt more supported on this journey. Recovery Connections is the real deal.",
    timeAgo: "3 years ago",
  },
  {
    name: "Sarah M.",
    initials: "SM",
    rating: 5,
    text: "The peer specialists meet you exactly where you are — no judgment, no shame, just compassion. They helped me find a path that fit my life.",
    timeAgo: "8 months ago",
  },
  {
    name: "James D.",
    initials: "JD",
    rating: 5,
    text: "I came in feeling alone and walked out with a community. The lived experience of every person on staff makes all the difference.",
    timeAgo: "5 months ago",
  },
];

const Stars = ({ count = 5, size = 14 }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        style={{ width: size, height: size }}
        className={
          i < count
            ? "fill-[#FBBC05] text-[#FBBC05]"
            : "fill-transparent text-[hsl(var(--border))]"
        }
      />
    ))}
  </div>
);

const GoogleLogo = ({ size = 16 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    aria-hidden="true"
  >
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
    />
  </svg>
);

const ReviewCard = ({ r }) => (
  <article className="rc-card-hover bg-white border border-[hsl(var(--border))] rounded-2xl p-7 flex flex-col h-full">
    <div className="flex items-start justify-between">
      <Stars count={r.rating} size={16} />
      <Quote className="w-5 h-5 text-[hsl(var(--rc-purple))]/30" />
    </div>
    <p className="mt-5 text-[15px] leading-[1.7] text-[hsl(var(--rc-charcoal))] flex-1">
      {r.text}
    </p>
    <div className="mt-6 pt-5 border-t border-[hsl(var(--border))] flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-[hsl(var(--rc-purple-soft))] flex items-center justify-center text-[hsl(var(--rc-purple))] text-[13px] font-semibold">
        {r.initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-[hsl(var(--rc-charcoal))] truncate">
          {r.name}
        </div>
        <div className="text-[11.5px] text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
          <GoogleLogo size={11} />
          Google Review · {r.timeAgo}
        </div>
      </div>
    </div>
  </article>
);

const GoogleReviews = () => {
  const [page, setPage] = useState(0);
  const perPage = 3;
  const totalPages = Math.ceil(REVIEWS.length / perPage);
  const visible = REVIEWS.slice(page * perPage, page * perPage + perPage);
  // Pad to 3 cards even if last page is short
  while (visible.length < perPage && page > 0) {
    visible.unshift(REVIEWS[REVIEWS.length - perPage + visible.length]);
  }

  const reviewsLink =
    "https://www.google.com/search?q=Recovery+Connections+of+Central+Florida+Reviews";

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-purple))] font-semibold">
              <GoogleLogo size={14} />
              Google Reviews
            </div>
            <h2 className="font-serif-display mt-3 text-[36px] md:text-[44px] leading-[1.1] font-semibold text-[hsl(var(--rc-deep))]">
              Hear from the community we serve.
            </h2>
            <p className="mt-4 text-[16px] leading-[1.7] text-[hsl(var(--rc-slate))] max-w-2xl">
              Real reflections from participants, families and partners across
              Central Florida. We are humbled by every word and stay grateful
              for the trust placed in our team.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[hsl(var(--rc-paper))] border border-[hsl(var(--border))] rounded-2xl p-6 md:p-7 flex items-center gap-5">
              <div className="text-center shrink-0">
                <div className="font-serif-display text-[44px] md:text-[52px] font-semibold leading-none text-[hsl(var(--rc-deep))]">
                  4.9
                </div>
                <div className="mt-1.5">
                  <Stars count={5} size={14} />
                </div>
              </div>
              <div className="flex-1 border-l border-[hsl(var(--border))] pl-5">
                <div className="flex items-center gap-2 text-[13px] font-semibold text-[hsl(var(--rc-charcoal))]">
                  <GoogleLogo size={14} /> Google Rating
                </div>
                <div className="mt-1 text-[13px] text-[hsl(var(--muted-foreground))]">
                  Based on <strong className="text-[hsl(var(--rc-deep))]">64 verified reviews</strong>
                </div>
                <a
                  href={reviewsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[hsl(var(--rc-purple))] hover:text-[hsl(var(--rc-deep))] transition-colors"
                >
                  Read all on Google
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Review cards */}
        <div className="grid md:grid-cols-3 gap-5">
          {visible.map((r, i) => (
            <ReviewCard key={`${page}-${i}-${r.name}`} r={r} />
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  aria-label={`Page ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${
                    page === i
                      ? "w-8 bg-[hsl(var(--rc-purple))]"
                      : "w-2 bg-[hsl(var(--border))] hover:bg-[hsl(var(--rc-purple))]/40"
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                aria-label="Previous reviews"
                className="w-10 h-10 rounded-full border border-[hsl(var(--border))] flex items-center justify-center text-[hsl(var(--rc-charcoal))] hover:bg-[hsl(var(--rc-purple-soft))] hover:border-[hsl(var(--rc-purple))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
                aria-label="Next reviews"
                className="w-10 h-10 rounded-full border border-[hsl(var(--border))] flex items-center justify-center text-[hsl(var(--rc-charcoal))] hover:bg-[hsl(var(--rc-purple-soft))] hover:border-[hsl(var(--rc-purple))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default GoogleReviews;
